import { ICloneable } from '../../contracts/cloneable.js';
import { ICopyWithBusinessRef } from '../../contracts/copy-with-business-ref.js';
import { IDataComparable } from '../../contracts/data-comparable.js';
import { isSameList, SerializationContext, UnknownDataModelTypePolicy, SerializedData } from '../../models/dto/dto.js';
import { BusinessModuleRef } from '../../models/serializable/business-module-ref.js';
import { MapUtils } from '../../utility/map-utils.js';
import { isNullOrUndefined } from '../../utility/null-safety.js';
import { Component } from './component.js';
import { IEntity } from './entity.js';
import { SerializationException } from '../../exceptions/serialization-exception.js';

export class ComponentList implements IEntity, ICloneable, IDataComparable, ICopyWithBusinessRef {
  private readonly components: Component[] = [];

  constructor(components?: Component[]) {
    if(components) {
      this.components.push(...components);
    }
  }

  static deserialize(context: SerializationContext, data: SerializedData[]): ComponentList {
    const components: Component[] = [];
    for(const item of data) {
      if(context.isSerializedData(item)) {
        const component = context.fromData<Component>(item);
        components.push(component);
      }
      else if(context.unknownTypePolicy === UnknownDataModelTypePolicy.Throw) {
        const typeId = MapUtils.getOrNull<string>(item, '$type_id');
        throw new SerializationException(
            // eslint-disable-next-line max-len
            `Unable to deserialize component list. Component data is not serializable: ${isNullOrUndefined(typeId) ? JSON.stringify(item) : typeId}`,
        );
      }
    }

    return new ComponentList(components);
  }

  serialize(context: SerializationContext): SerializedData[] {
    return this.components.map((e) => context.toData(e));
  }

  any(test: (c: Component) => boolean): boolean {
    for(let i = 0; i < this.components.length; i++) {
      if(test(this.components[i])) {
        return true;
      }
    }

    return false;
  }

  forEach(action: (c: Component) => void): void {
    for(let i = 0; i < this.components.length; i++) {
      action(this.components[i]);
    }
  }

  hasComponentByTypeId(typeId: string): boolean {
    for(let i = 0; i < this.components.length; i++) {
      if(this.components[i].$typeId === typeId) {
        return true;
      }
    }

    return false;
  }

  mustFindComponentByTypeId<C extends Component>(typeId: string): C {
    const component = this.findComponentByTypeId<C>(typeId);
    if(!component) {
      throw Error(`Component not found with type ID: ${typeId}`);
    }

    return component;
  }

  findComponentByTypeId<C extends Component>(typeId: string): C | null {
    for(let i = 0; i < this.components.length; i++) {
      if(this.components[i].$typeId === typeId) {
        return this.components[i] as C;
      }
    }

    return null;
  }

  findOrCreateComponentByTypeId<C extends Component>(typeId: string, creator: () => C): C {
    for(let i = 0; i < this.components.length; i++) {
      if(this.components[i].$typeId === typeId) {
        return this.components[i] as C;
      }
    }

    const c = creator();
    this.components.push(c);

    return c;
  }

  findComponentByInstanceId<C extends Component>(instanceId: string): C | null {
    for(let i = 0; i < this.components.length; i++) {
      if(this.components[i].$instanceId === instanceId) {
        return this.components[i] as C;
      }
    }

    return null;
  }

  findComponentsByTypeId<C extends Component>(typeId: string): C[] {
    const components: C[] = [];

    for(let i = 0; i < this.components.length; i++) {
      if(this.components[i].$typeId === typeId) {
        components.push(this.components[i] as C);
      }
    }

    return components;
  }

  addComponent<C extends Component>(component: C): void {
    this.components.push(component);
  }

  addComponents(list: Component[]): void {
    this.components.push(...list);
  }

  removeComponentByTypeId(typeId: string): boolean {
    for(let i = 0; i < this.components.length; i++) {
      if(this.components[i].$typeId === typeId) {
        this.components.splice(i, 1);
        return true;
      }
    }

    return false;
  }

  removeComponentByInstanceId(instanceId: string): boolean {
    for(let i = 0; i < this.components.length; i++) {
      if(this.components[i].$instanceId === instanceId) {
        this.components.splice(i, 1);
        return true;
      }
    }

    return false;
  }

  removeComponentAt(index: number): void {
    if(index < 0 || index >= this.components.length) {
      throw new RangeError(
          `Component index is out of range. \
           Expected an index between {0} and {${this.components.length}} but got {${index}}.`,
      );
    }

    this.components.splice(index, 1);
  }

  removeAllComponents(): void {
    this.components.length = 0;
  }

  popAllComponentsByTypeId<C extends Component>(typeId: string): C[] {
    const components: C[] = [];

    for(let i = this.components.length - 1; i >= 0; i--) {
      if(this.components[i].$typeId === typeId) {
        components.push(this.components[i] as C);
        this.components.splice(i, 1);
      }
    }

    return components;
  }

  popComponentByTypeId<C extends Component>(typeId: string): C | null {
    for(let i = 0; i < this.components.length; i++) {
      if(this.components[i].$typeId === typeId) {
        const component = this.components[i];
        this.components.splice(i, 1);

        return component as C;
      }
    }

    return null;
  }

  popComponent<C extends Component>(component: C): C | null {
    for(let i = 0; i < this.components.length; i++) {
      if(this.components[i] === component) {
        this.components.splice(i, 1);
        return component;
      }
    }

    return null;
  }

  replaceComponent<C extends Component>(replacement: C): boolean {
    for(let i = 0; i < this.components.length; i++) {
      if(this.components[i].$typeId === replacement.$typeId) {
        this.components[i] = replacement;
        return true;
      }
    }

    return false;
  }

  addOrReplaceComponent<C extends Component>(component: C): void {
    for(let i = 0; i < this.components.length; i++) {
      if(this.components[i].$typeId == component.$typeId) {
        this.components[i] = component;
        return;
      }
    }

    this.components.push(component);
  }

  mustFindComponentField<C extends Component, F>(componentTypeId: string, find: (c: C) => F): F {
    const component = this.mustFindComponentByTypeId<C>(componentTypeId);
    return find(component);
  }

  findComponentField<C extends Component, F>(componentTypeId: string, find: (c: C) => F): F | null {
    const component = this.findComponentByTypeId<C>(componentTypeId);
    if(isNullOrUndefined(component)) {
      return null;
    }

    return find(component);
  }

  clone(): this {
    return new ComponentList(this.components.map((e) => e.clone())) as this;
  }

  copyWithBusinessRef(businessRef: BusinessModuleRef): this {
    const components: Component[] = [];

    for(let i = 0; i < this.components.length; i++) {
      const component = this.components[i];

      if(ICopyWithBusinessRef.isInstance(component)) {
        components.push(component.copyWithBusinessRef(businessRef));
      }
      else {
        components.push(component.clone());
      }
    }

    return new ComponentList(components) as this;
  }

  isSame(other: ComponentList): boolean {
    if(this === other) {
      return true;
    }

    return isSameList<Component>(other.components, this.components, (a, b) => a.isSame(b));
  }
}
